import React, { useEffect, useMemo, useState } from "react";
import { Box, Checkbox, CircularProgress, Collapse, Divider, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Api from "../../api/Api";
import { Urls } from "../../api/Urls";
import { toast } from "react-toastify";

/**
 * A simple tree-style multi selector for audience segments:
 * - All Clients
 * - All Coaches
 * - Programs (list)
 * - Private Lessons (group)
 * - Court Bookings (group)
 *
 * onChange receives an array of segments:
 *   [{ type: "all_clients" }, { type: "program_duration", identifiers: [3] }]
 */
export default function AudienceTreeSelect({ access_token, onChange }) {
  const [openNodes, setOpenNodes] = useState({
    root_clients: false,
    root_coaches: false,
    root_admins: false,
    root_programs: false,
    root_private_lessons: false,
    root_court_bookings: false,
  });
  const [checked, setChecked] = useState({
    all_clients: false,
    all_coaches: false,
    all_admins: false,
    programs: new Set(),
    private_lessons: false,
    court_bookings: false,
  });

  const [programs, setPrograms] = useState([]);
  const [weeklySchedules, setWeeklySchedules] = useState([]); // legacy, will not be used
  const [loading, setLoading] = useState(false);
  const [loadingUsersFor, setLoadingUsersFor] = useState({});
  const [usersByNode, setUsersByNode] = useState({}); // key -> [{id,name,email,role}]
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());

  useEffect(() => {
    setLoading(true);
    Promise.all([
      Api.get(Urls.GET_PROGRAMS, {
        headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
      }).then((res) => res?.data?.data || res?.data || []),
      Api.get(Urls.GET_PLANNING, {
        headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
      }).then((res) => res?.data?.data || []),
    ])
      .then(([programsData, schedulesData]) => {
        setPrograms(programsData);
        setWeeklySchedules(schedulesData);
        setLoading(false);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Error loading audience lists");
        setLoading(false);
      });
  }, [access_token]);

  const toggleNode = (key) => setOpenNodes((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleCheckRoot = (key) => {
    const turnedOn = !checked[key];
    setChecked((prev) => ({ ...prev, [key]: turnedOn }));

    // Map to internal node key and target type
    const nodeKey =
      key === "all_clients" ? "clients" : key === "all_coaches" ? "coaches" : key === "all_admins" ? "admins" : null;
    const type =
      key === "all_clients" ? "all_clients" : key === "all_coaches" ? "all_coaches" : key === "all_admins" ? "all_admins" : null;

    if (nodeKey && type) {
      if (turnedOn) {
        // Select all for this group (load if needed)
        if (usersByNode[nodeKey]) {
          const ids = usersByNode[nodeKey].map((u) => u.id);
          setSelectedUserIds((prev) => new Set([...prev, ...ids]));
        } else {
          // Ensure node is open to show loading, then fetch and select all
          setOpenNodes((prev) => ({ ...prev, [`root_${nodeKey}`]: true }));
          fetchUsersForNode(nodeKey, { targets: [{ type }] }, true);
        }
      } else {
        // Deselect all for this group if loaded
        if (usersByNode[nodeKey]) {
          const idsToRemove = new Set(usersByNode[nodeKey].map((u) => u.id));
          setSelectedUserIds((prev) => {
            const next = new Set(prev);
            idsToRemove.forEach((id) => next.delete(id));
            return next;
          });
        }
      }
    }
  };

  const handleToggleId = (setKey, id) => {
    setChecked((prev) => {
      const nextSet = new Set(prev[setKey]);
      if (nextSet.has(id)) nextSet.delete(id);
      else nextSet.add(id);
      return { ...prev, [setKey]: nextSet };
    });
  };

  const segments = useMemo(() => {
    const segs = [];
    if (checked.all_clients) segs.push({ type: "all_clients" });
    if (checked.all_coaches) segs.push({ type: "all_coaches" });
    if (checked.programs.size > 0) {
      segs.push({
        type: "program_duration",
        identifiers: Array.from(checked.programs),
      });
    }
    // private_lessons and court_bookings are resolved to users; segments not required
    return segs;
  }, [checked]);

  // Fetch users when a specific node is expanded
  const fetchUsersForNode = (key, payload, selectAllAfterLoad = false) => {
    setLoadingUsersFor((prev) => ({ ...prev, [key]: true }));
    Api.post(
      Urls.ESTIMATE_NOTIFICATION_AUDIENCE.replace("estimate-audience", "resolve-audience"),
      payload,
      { headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` } }
    )
      .then((res) => {
        const users = res?.data?.data?.users || [];
        setUsersByNode((prev) => ({ ...prev, [key]: users }));
        if (selectAllAfterLoad && users.length > 0) {
          const ids = users.map((u) => u.id);
          setSelectedUserIds((prev) => new Set([...prev, ...ids]));
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Error loading recipients");
      })
      .finally(() =>
        setLoadingUsersFor((prev) => {
          const c = { ...prev };
          delete c[key];
          return c;
        })
      );
  };

  // Compute selection state for group checkboxes (shows "-" when partially selected)
  const computeGroupState = (nodeKey, fallbackChecked) => {
    const users = usersByNode[nodeKey];
    if (!users || users.length === 0) {
      return { checked: !!fallbackChecked, indeterminate: false };
    }
    const total = users.length;
    const selected = users.reduce((acc, u) => acc + (selectedUserIds.has(u.id) ? 1 : 0), 0);
    if (selected === 0) return { checked: false, indeterminate: false };
    if (selected === total) return { checked: true, indeterminate: false };
    return { checked: false, indeterminate: true };
  };

  const computeAggregateStateFor = (nodeKeys, fallbackNoneChecked, fallbackAllChecked) => {
    // If some nodes are loaded, compute based on loaded users
    let total = 0;
    let selected = 0;
    nodeKeys.forEach((k) => {
      const users = usersByNode[k];
      if (users && users.length > 0) {
        total += users.length;
        users.forEach((u) => {
          if (selectedUserIds.has(u.id)) selected += 1;
        });
      }
    });
    if (total > 0) {
      if (selected === 0) return { checked: false, indeterminate: false };
      if (selected === total) return { checked: true, indeterminate: false };
      return { checked: false, indeterminate: true };
    }
    // Fallback to checkbox sets when nothing loaded
    if (fallbackNoneChecked) return { checked: false, indeterminate: false };
    if (fallbackAllChecked) return { checked: true, indeterminate: false };
    return { checked: false, indeterminate: true };
  };

  useEffect(() => {
    // Expose both: selected users and current segment selection
    onChange?.({
      users: Array.from(selectedUserIds),
      segments,
    });
  }, [selectedUserIds, segments, onChange]);

  return (
    <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1, overflow: "hidden" }}>
      <Box sx={{ p: 1.5 }}>
        <Typography variant="subtitle2">Recipients</Typography>
      </Box>
      <Divider />
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <List dense disablePadding>
            {/* All Clients */}
            <ListItem
              secondaryAction={
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => {
                    const willOpen = !openNodes.root_clients;
                    toggleNode("root_clients");
                    if (willOpen && !usersByNode["clients"]) {
                      fetchUsersForNode("clients", { targets: [{ type: "all_clients" }] });
                    }
                  }}
                >
                  {openNodes.root_clients ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                </IconButton>
              }
            >
              <ListItemIcon>
                {(() => {
                  const state = computeGroupState("clients", checked.all_clients);
                  return (
                    <Checkbox
                  edge="start"
                  checked={state.checked}
                  indeterminate={state.indeterminate}
                  onChange={() => handleCheckRoot("all_clients")}
                    />
                  );
                })()}
              </ListItemIcon>
              <ListItemText primary="All Clients" />
            </ListItem>
            <Collapse in={openNodes.root_clients} timeout="auto" unmountOnExit>
              <List dense disablePadding sx={{ pl: 5 }}>
                {loadingUsersFor["clients"] ? (
                  <ListItem>
                    <ListItemText primary={<CircularProgress size={18} />} />
                  </ListItem>
                ) : usersByNode["clients"] ? (
                  usersByNode["clients"].map((u) => {
                    const isChecked = selectedUserIds.has(u.id);
                    return (
                      <ListItemButton
                        key={u.id}
                        onClick={() =>
                          setSelectedUserIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(u.id)) next.delete(u.id);
                            else next.add(u.id);
                            return next;
                          })
                        }
                      >
                        <ListItemIcon>
                          <Checkbox edge="start" checked={isChecked} tabIndex={-1} />
                        </ListItemIcon>
                        <ListItemText
                          primary={u.name || `User #${u.id}`}
                          secondary={u.email ? `${u.email}${u.role ? ` • ${u.role}` : ""}` : u.role}
                        />
                      </ListItemButton>
                    );
                  })
                ) : (
                  <ListItem>
                    <ListItemText
                      primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
                      primary="Expand to load clients..."
                    />
                  </ListItem>
                )}
              </List>
            </Collapse>

            {/* All Coaches */}
            <ListItem
              secondaryAction={
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => {
                    const willOpen = !openNodes.root_coaches;
                    toggleNode("root_coaches");
                    if (willOpen && !usersByNode["coaches"]) {
                      fetchUsersForNode("coaches", { targets: [{ type: "all_coaches" }] });
                    }
                  }}
                >
                  {openNodes.root_coaches ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                </IconButton>
              }
            >
              <ListItemIcon>
                {(() => {
                  const state = computeGroupState("coaches", checked.all_coaches);
                  return (
                    <Checkbox
                  edge="start"
                  checked={state.checked}
                  indeterminate={state.indeterminate}
                  onChange={() => handleCheckRoot("all_coaches")}
                    />
                  );
                })()}
              </ListItemIcon>
              <ListItemText primary="All Coaches" />
            </ListItem>
            <Collapse in={openNodes.root_coaches} timeout="auto" unmountOnExit>
              <List dense disablePadding sx={{ pl: 5 }}>
                {loadingUsersFor["coaches"] ? (
                  <ListItem>
                    <ListItemText primary={<CircularProgress size={18} />} />
                  </ListItem>
                ) : usersByNode["coaches"] ? (
                  usersByNode["coaches"].map((u) => {
                    const isChecked = selectedUserIds.has(u.id);
                    return (
                      <ListItemButton
                        key={u.id}
                        onClick={() =>
                          setSelectedUserIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(u.id)) next.delete(u.id);
                            else next.add(u.id);
                            return next;
                          })
                        }
                      >
                        <ListItemIcon>
                          <Checkbox edge="start" checked={isChecked} tabIndex={-1} />
                        </ListItemIcon>
                        <ListItemText
                          primary={u.name || `User #${u.id}`}
                          secondary={u.email ? `${u.email}${u.role ? ` • ${u.role}` : ""}` : u.role}
                        />
                      </ListItemButton>
                    );
                  })
                ) : (
                  <ListItem>
                    <ListItemText
                      primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
                      primary="Expand to load coaches..."
                    />
                  </ListItem>
                )}
              </List>
            </Collapse>

            {/* All Admins */}
            <ListItem
              secondaryAction={
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => {
                    const willOpen = !openNodes.root_admins;
                    setOpenNodes((prev) => ({ ...prev, root_admins: willOpen }));
                    if (willOpen && !usersByNode["admins"]) {
                      fetchUsersForNode("admins", { targets: [{ type: "all_admins" }] });
                    }
                  }}
                >
                  {openNodes.root_admins ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                </IconButton>
              }
            >
              <ListItemIcon>
                {(() => {
                  const state = computeGroupState("admins", checked.all_admins);
                  return (
                    <Checkbox
                      edge="start"
                      checked={state.checked}
                      indeterminate={state.indeterminate}
                      onChange={() => handleCheckRoot("all_admins")}
                    />
                  );
                })()}
              </ListItemIcon>
              <ListItemText primary="All Admins" />
            </ListItem>
            <Collapse in={openNodes.root_admins} timeout="auto" unmountOnExit>
              <List dense disablePadding sx={{ pl: 5 }}>
                {loadingUsersFor["admins"] ? (
                  <ListItem>
                    <ListItemText primary={<CircularProgress size={18} />} />
                  </ListItem>
                ) : usersByNode["admins"] ? (
                  usersByNode["admins"].map((u) => {
                    const isChecked = selectedUserIds.has(u.id);
                    return (
                      <ListItemButton
                        key={u.id}
                        onClick={() =>
                          setSelectedUserIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(u.id)) next.delete(u.id);
                            else next.add(u.id);
                            return next;
                          })
                        }
                      >
                        <ListItemIcon>
                          <Checkbox edge="start" checked={isChecked} tabIndex={-1} />
                        </ListItemIcon>
                        <ListItemText
                          primary={u.name || `User #${u.id}`}
                          secondary={u.email ? `${u.email}${u.role ? ` • ${u.role}` : ""}` : u.role}
                        />
                      </ListItemButton>
                    );
                  })
                ) : (
                  <ListItem>
                    <ListItemText
                      primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
                      primary="Expand to load admins..."
                    />
                  </ListItem>
                )}
              </List>
            </Collapse>

            {/* Programs */}
            <ListItem
              secondaryAction={
                <IconButton edge="end" size="small" onClick={() => toggleNode("root_programs")}>
                  {openNodes.root_programs ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                </IconButton>
              }
            >
              <ListItemIcon>
                {(() => {
                  const nodeKeys = programs.map((p) => `program_${p.id}`);
                  const state = computeAggregateStateFor(
                    nodeKeys,
                    checked.programs.size === 0,
                    programs.length > 0 && checked.programs.size === programs.length
                  );
                  return (
                    <Checkbox
                      edge="start"
                      checked={state.checked}
                      indeterminate={state.indeterminate}
                      onChange={() => {
                        const turnOn = !(programs.length > 0 && checked.programs.size === programs.length);
                        if (turnOn) {
                          // select all programs and users
                          setChecked((prev) => ({ ...prev, programs: new Set(programs.map((p) => p.id)) }));
                          programs.forEach((p) => {
                            const key = `program_${p.id}`;
                            if (usersByNode[key]) {
                              const ids = usersByNode[key].map((u) => u.id);
                              setSelectedUserIds((prev) => new Set([...prev, ...ids]));
                            } else {
                              fetchUsersForNode(key, { targets: [{ type: "program_duration", identifiers: [p.id] }] }, true);
                            }
                          });
                        } else {
                          // deselect all programs and loaded users
                          setChecked((prev) => ({ ...prev, programs: new Set() }));
                          const idsToRemove = new Set();
                          programs.forEach((p) => {
                            const key = `program_${p.id}`;
                            (usersByNode[key] || []).forEach((u) => idsToRemove.add(u.id));
                          });
                          setSelectedUserIds((prev) => {
                            const next = new Set(prev);
                            idsToRemove.forEach((id) => next.delete(id));
                            return next;
                          });
                        }
                      }}
                    />
                  );
                })()}
              </ListItemIcon>
              <ListItemText primary="Programs" />
            </ListItem>
            <Collapse in={openNodes.root_programs} timeout="auto" unmountOnExit>
              <List dense disablePadding sx={{ pl: 5 }}>
                {programs.map((p) => (
                  <React.Fragment key={p.id}>
                    <ListItem
                      secondaryAction={
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => {
                            const key = `program_${p.id}`;
                            const willOpen = !(openNodes[key] || false);
                            setOpenNodes((prev) => ({ ...prev, [key]: willOpen }));
                            if (willOpen && !usersByNode[key]) {
                              fetchUsersForNode(key, { targets: [{ type: "program_duration", identifiers: [p.id] }] });
                            }
                          }}
                        >
                          {openNodes[`program_${p.id}`] ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                        </IconButton>
                      }
                    >
                      <ListItemIcon>
                        {(() => {
                          const state = computeGroupState(`program_${p.id}`, checked.programs.has(p.id));
                          return (
                            <Checkbox
                              edge="start"
                              checked={state.checked}
                              indeterminate={state.indeterminate}
                              onChange={() => {
                                const turnOn = !checked.programs.has(p.id);
                                setChecked((prev) => {
                                  const next = new Set(prev.programs);
                                  if (turnOn) next.add(p.id);
                                  else next.delete(p.id);
                                  return { ...prev, programs: next };
                                });
                                const key = `program_${p.id}`;
                                if (turnOn) {
                                  if (usersByNode[key]) {
                                    const ids = usersByNode[key].map((u) => u.id);
                                    setSelectedUserIds((prev) => new Set([...prev, ...ids]));
                                  } else {
                                    fetchUsersForNode(key, { targets: [{ type: "program_duration", identifiers: [p.id] }] }, true);
                                  }
                                } else {
                                  const idsToRemove = new Set((usersByNode[key] || []).map((u) => u.id));
                                  setSelectedUserIds((prev) => {
                                    const next = new Set(prev);
                                    idsToRemove.forEach((id) => next.delete(id));
                                    return next;
                                  });
                                }
                              }}
                            />
                          );
                        })()}
                      </ListItemIcon>
                      <ListItemText primary={p.name} />
                    </ListItem>
                    <Collapse in={openNodes[`program_${p.id}`]} timeout="auto" unmountOnExit>
                      <List dense disablePadding sx={{ pl: 5 }}>
                        {loadingUsersFor[`program_${p.id}`] ? (
                          <ListItem>
                            <ListItemText primary={<CircularProgress size={18} />} />
                          </ListItem>
                        ) : usersByNode[`program_${p.id}`] ? (
                          usersByNode[`program_${p.id}`].map((u) => {
                            const isChecked = selectedUserIds.has(u.id);
                            return (
                              <ListItemButton
                                key={u.id}
                                onClick={() =>
                                  setSelectedUserIds((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(u.id)) next.delete(u.id);
                                    else next.add(u.id);
                                    return next;
                                  })
                                }
                              >
                                <ListItemIcon>
                                  <Checkbox edge="start" checked={isChecked} tabIndex={-1} />
                                </ListItemIcon>
                                <ListItemText
                                  primary={u.name || `User #${u.id}`}
                                  secondary={
                                    u.email ? `${u.email}${u.role ? ` • ${u.role}` : ""}` : u.role
                                  }
                                />
                              </ListItemButton>
                            );
                          })
                        ) : (
                          <ListItem>
                            <ListItemText primaryTypographyProps={{ variant: "body2", color: "text.secondary" }} primary="No users" />
                          </ListItem>
                        )}
                      </List>
                    </Collapse>
                  </React.Fragment>
                ))}
              </List>
            </Collapse>

            {/* Private Lessons */}
            <ListItem
              secondaryAction={
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => {
                    const willOpen = !openNodes.root_private_lessons;
                    setOpenNodes((prev) => ({ ...prev, root_private_lessons: willOpen }));
                    if (willOpen && !usersByNode["private_lessons"]) {
                      fetchUsersForNode("private_lessons", { targets: [{ type: "private_lesson" }] });
                    }
                  }}
                >
                  {openNodes.root_private_lessons ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                </IconButton>
              }
            >
              <ListItemIcon>
                {(() => {
                  const state = computeGroupState("private_lessons", checked.private_lessons);
                  return (
                    <Checkbox
                      edge="start"
                      checked={state.checked}
                      indeterminate={state.indeterminate}
                      onChange={() => {
                        const turnOn = !checked.private_lessons;
                        setChecked((prev) => ({ ...prev, private_lessons: turnOn }));
                        if (turnOn) {
                          if (usersByNode["private_lessons"]) {
                            const ids = usersByNode["private_lessons"].map((u) => u.id);
                            setSelectedUserIds((prev) => new Set([...prev, ...ids]));
                          } else {
                            setOpenNodes((prev) => ({ ...prev, root_private_lessons: true }));
                            fetchUsersForNode("private_lessons", { targets: [{ type: "private_lesson" }] }, true);
                          }
                        } else {
                          const idsToRemove = new Set((usersByNode["private_lessons"] || []).map((u) => u.id));
                          setSelectedUserIds((prev) => {
                            const next = new Set(prev);
                            idsToRemove.forEach((id) => next.delete(id));
                            return next;
                          });
                        }
                      }}
                    />
                  );
                })()}
              </ListItemIcon>
              <ListItemText primary="Private Lessons" />
            </ListItem>
            <Collapse in={openNodes.root_private_lessons} timeout="auto" unmountOnExit>
              <List dense disablePadding sx={{ pl: 5 }}>
                {(usersByNode["private_lessons"] || []).map((u) => {
                  const isChecked = selectedUserIds.has(u.id);
                  return (
                    <ListItemButton
                      key={u.id}
                      onClick={() =>
                        setSelectedUserIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(u.id)) next.delete(u.id);
                          else next.add(u.id);
                          return next;
                        })
                      }
                    >
                      <ListItemIcon>
                        <Checkbox edge="start" checked={isChecked} tabIndex={-1} />
                      </ListItemIcon>
                      <ListItemText
                        primary={u.name || `User #${u.id}`}
                        secondary={u.email ? `${u.email}${u.role ? ` • ${u.role}` : ""}` : u.role}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>

            {/* Court Bookings */}
            <ListItem
              secondaryAction={
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => {
                    const willOpen = !openNodes.root_court_bookings;
                    setOpenNodes((prev) => ({ ...prev, root_court_bookings: willOpen }));
                    if (willOpen && !usersByNode["court_bookings"]) {
                      fetchUsersForNode("court_bookings", { targets: [{ type: "court_booking" }] });
                    }
                  }}
                >
                  {openNodes.root_court_bookings ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                </IconButton>
              }
            >
              <ListItemIcon>
                {(() => {
                  const state = computeGroupState("court_bookings", checked.court_bookings);
                  return (
                    <Checkbox
                      edge="start"
                      checked={state.checked}
                      indeterminate={state.indeterminate}
                      onChange={() => {
                        const turnOn = !checked.court_bookings;
                        setChecked((prev) => ({ ...prev, court_bookings: turnOn }));
                        if (turnOn) {
                          if (usersByNode["court_bookings"]) {
                            const ids = usersByNode["court_bookings"].map((u) => u.id);
                            setSelectedUserIds((prev) => new Set([...prev, ...ids]));
                          } else {
                            setOpenNodes((prev) => ({ ...prev, root_court_bookings: true }));
                            fetchUsersForNode("court_bookings", { targets: [{ type: "court_booking" }] }, true);
                          }
                        } else {
                          const idsToRemove = new Set((usersByNode["court_bookings"] || []).map((u) => u.id));
                          setSelectedUserIds((prev) => {
                            const next = new Set(prev);
                            idsToRemove.forEach((id) => next.delete(id));
                            return next;
                          });
                        }
                      }}
                    />
                  );
                })()}
              </ListItemIcon>
              <ListItemText primary="Court Bookings" />
            </ListItem>
            <Collapse in={openNodes.root_court_bookings} timeout="auto" unmountOnExit>
              <List dense disablePadding sx={{ pl: 5 }}>
                {(usersByNode["court_bookings"] || []).map((u) => {
                  const isChecked = selectedUserIds.has(u.id);
                  return (
                    <ListItemButton
                      key={u.id}
                      onClick={() =>
                        setSelectedUserIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(u.id)) next.delete(u.id);
                          else next.add(u.id);
                          return next;
                        })
                      }
                    >
                      <ListItemIcon>
                        <Checkbox edge="start" checked={isChecked} tabIndex={-1} />
                      </ListItemIcon>
                      <ListItemText
                        primary={u.name || `User #${u.id}`}
                        secondary={u.email ? `${u.email}${u.role ? ` • ${u.role}` : ""}` : u.role}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>
        </List>
      )}
    </Box>
  );
}


